import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import * as inboxApi from '../api/inbox';
import { useAuth } from './auth';

const POLL_INTERVAL_MS = 60_000;

const MessagingContext = createContext(null);

export function MessagingProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const liveRef = useRef(false);
  useEffect(() => {
    liveRef.current = isAuthenticated;
  }, [isAuthenticated]);
  useEffect(
    () => () => {
      liveRef.current = false;
    },
    []
  );

  const refresh = useCallback(async () => {
    if (!liveRef.current) return;
    try {
      const data = await inboxApi.getUnreadCount();
      if (!liveRef.current) return;
      if (data && typeof data.unreadCount === 'number') setUnreadCount(data.unreadCount);
    } catch {
      // Silent by design.
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setUnreadCount(0);
      return undefined;
    }
    refresh();

    const timer = setInterval(refresh, POLL_INTERVAL_MS);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [isAuthenticated, refresh]);

  const value = useMemo(() => ({ unreadCount, refresh }), [unreadCount, refresh]);

  return (
    <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>
  );
}

export function useMessaging() {
  const ctx = useContext(MessagingContext);
  if (!ctx) throw new Error('useMessaging must be used inside <MessagingProvider>');
  return ctx;
}
